import { useMutation, useQuery } from '@apollo/client/react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';

import TabPanel from '../../TabPanel';
import { type SettingsModalTabProps } from '../../types';

import RoleEditorModal, { type RoleDraft } from './RoleEditorModal';

import RequestPermissionTooltip from '@/components/RequestPermissionTooltip';
import PermissionContext from '@/Contexts/Permissions';
import {
  CreateRoleDocument,
  DeleteRoleDocument,
  GetRolesDocument,
  type GetRolesQuery,
  PermissionActionEnum,
  PermissionEntityEnum,
  UpdateRoleDocument,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

type Role = GetRolesQuery['getRoles'][number];

const RolesTab: React.FC<SettingsModalTabProps> = ({ parentTab, index }) => {
  const { data, loading, refetch } = useQuery(GetRolesDocument, { fetchPolicy: 'cache-and-network' });
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { PermissionManagement } = PermissionEntityEnum;

  const [createRole] = useMutation(CreateRoleDocument);
  const [updateRole] = useMutation(UpdateRoleDocument);
  const [deleteRole] = useMutation(DeleteRoleDocument);

  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const roles = data?.getRoles ?? [];
  const hasPermission = canPerformAction(Manage, PermissionManagement);
  const sharedPermissionTooltipProps = {
    hasPermission,
    requiredPermission: { action: Manage, entity: PermissionManagement },
    checkRequestExists,
    onRequest: requestPermission,
  };

  const handleSave = async (draft: RoleDraft) => {
    setSaving(true);
    try {
      if (editing) {
        await updateRole({
          variables: {
            id: editing.id,
            name: draft.name,
            description: draft.description,
            grants: draft.grants,
          },
        });
      } else {
        await createRole({
          variables: { name: draft.name, description: draft.description, grants: draft.grants },
        });
      }
      await refetch();
      displayNotification({ type: 'success', text: `Role ${editing ? 'updated' : 'created'}.` });
      setEditing(null);
      setCreating(false);
    } catch (error) {
      displayNotification({
        type: 'error',
        text: error instanceof Error ? error.message : 'The role could not be saved.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: Role) => {
    const confirmed = window.confirm(
      role.membersCount > 0
        ? `${role.name} is held by ${role.membersCount} user(s). They will lose the permissions it grants. Delete it?`
        : `Delete the role ${role.name}?`,
    );
    if (!confirmed) return;

    try {
      await deleteRole({ variables: { id: role.id } });
      await refetch();
      displayNotification({ type: 'success', text: 'Role deleted.' });
    } catch {
      displayNotification({ type: 'error', text: 'The role could not be deleted.' });
    }
  };

  return (
    <TabPanel value={parentTab} index={index} height="100%" containerSx={{ height: '100%' }}>
      <Box sx={{ p: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              Roles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A role bundles permissions. Editing one updates everyone who holds it.
            </Typography>
          </Box>
          <RequestPermissionTooltip {...sharedPermissionTooltipProps}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              disabled={!canPerformAction(Manage, PermissionManagement)}
            >
              New role
            </Button>
          </RequestPermissionTooltip>
        </Box>

        {loading && roles.length === 0 ? (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading roles…
            </Typography>
          </Box>
        ) : roles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No roles yet. Create one to grant a consistent set of permissions to a group of people.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {roles.map((role) => (
              <Grid size="auto" maxWidth={350} key={`role-card-${role.name}`}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {role.name}
                        </Typography>
                        {role.description && (
                          <Typography variant="body2" color="text.secondary">
                            {role.description}
                          </Typography>
                        )}
                      </Box>
                      <RequestPermissionTooltip {...sharedPermissionTooltipProps} title="Edit role">
                        <IconButton size="small" onClick={() => setEditing(role)} disabled={!hasPermission}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </RequestPermissionTooltip>
                      <RequestPermissionTooltip {...sharedPermissionTooltipProps} title="Delete role">
                        <IconButton size="small" onClick={() => handleDelete(role)} disabled={!hasPermission}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </RequestPermissionTooltip>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                      <Chip size="small" label={`${role.grants.length} permissions`} />
                      <Chip size="small" variant="outlined" label={`${role.membersCount} member(s)`} />
                      <Box sx={{ flex: 1 }} />
                      <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                        {role.members.map((member) => (
                          <Tooltip key={member.id} title={member.fullName ?? ''} arrow>
                            <Avatar src={member.imageUrl ?? undefined}>{member.fullName?.[0]}</Avatar>
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {(creating || editing) && (
        <RoleEditorModal
          key={editing?.id ?? 'new'}
          open
          role={editing}
          saving={saving}
          handleClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
    </TabPanel>
  );
};

export default RolesTab;
